import { useEffect, useRef } from "react";
import { Bold, Italic, Underline, List, ListOrdered, AArrowDown, AArrowUp } from "lucide-react";

const ToolBtn = ({ icon: Icon, label, onClick, testid }) => (
  <button type="button" onMouseDown={(e) => { e.preventDefault(); onClick(); }}
    title={label} data-testid={testid}
    className="p-1.5 rounded-lg text-[#4A4B4A] hover:bg-[#F3F2EE] transition-colors">
    <Icon className="w-3.5 h-3.5" />
  </button>
);

/**
 * Rich text editor for letter templates. `editorKey` reloads the content when a different
 * template is opened, so typing never resets the cursor.
 */
export default function LetterBodyEditor({ value, onChange, placeholders = [], editorKey = "", testid = "letter-body-editor" }) {
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current) ref.current.innerHTML = value || "";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorKey]);

  const emit = () => onChange(ref.current?.innerHTML || "");

  const exec = (cmd, val) => {
    ref.current?.focus();
    document.execCommand(cmd, false, val);
    emit();
  };

  const changeFontSize = (dir) => {
    ref.current?.focus();
    const current = Number(document.queryCommandValue("fontSize")) || 3;
    exec("fontSize", String(Math.min(6, Math.max(1, current + dir))));
  };

  return (
    <div className="rounded-xl border border-[#1A1B1A]/10 overflow-hidden">
      <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-[#1A1B1A]/[0.06] bg-[#FAF9F6]">
        <ToolBtn icon={Bold} label="Bold" onClick={() => exec("bold")} testid={`${testid}-bold`} />
        <ToolBtn icon={Italic} label="Italic" onClick={() => exec("italic")} testid={`${testid}-italic`} />
        <ToolBtn icon={Underline} label="Underline" onClick={() => exec("underline")} testid={`${testid}-underline`} />
        <span className="w-px h-4 bg-[#1A1B1A]/10 mx-1" />
        <select
          onChange={(e) => { exec("formatBlock", e.target.value); e.target.selectedIndex = 0; }}
          data-testid={`${testid}-text-style`}
          className="text-[11px] px-2 py-1 rounded-lg border border-[#1A1B1A]/10 bg-white text-[#4A4B4A]"
        >
          <option value="">Text style</option>
          <option value="<p>">Normal text</option>
          <option value="<h2>">Heading</option>
          <option value="<h3>">Subheading</option>
        </select>
        <ToolBtn icon={AArrowDown} label="Smaller text" onClick={() => changeFontSize(-1)} testid={`${testid}-font-smaller`} />
        <ToolBtn icon={AArrowUp} label="Larger text" onClick={() => changeFontSize(1)} testid={`${testid}-font-larger`} />
        <span className="w-px h-4 bg-[#1A1B1A]/10 mx-1" />
        <ToolBtn icon={List} label="Bullet list" onClick={() => exec("insertUnorderedList")} testid={`${testid}-bullets`} />
        <ToolBtn icon={ListOrdered} label="Numbered list" onClick={() => exec("insertOrderedList")} testid={`${testid}-numbers`} />
      </div>

      {placeholders.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 px-2 py-2 border-b border-[#1A1B1A]/[0.06] bg-white">
          <span className="text-[10px] text-[#757675] mr-1">Insert:</span>
          {placeholders.map((p) => (
            <button key={p} type="button" title={`Insert ${p}`}
              data-testid={`insert-placeholder-${p.replace(/[{}]/g, "")}`}
              onMouseDown={(e) => { e.preventDefault(); exec("insertText", p); }}
              className="text-[10px] font-mono px-2 py-1 rounded-lg bg-[#205B5A]/[0.06] text-[#205B5A] hover:bg-[#205B5A]/[0.12]">
              {p.replace(/[{}]/g, "").replace(/_/g, " ")}
            </button>
          ))}
        </div>
      )}

      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        onBlur={emit}
        data-testid={testid}
        className="min-h-[240px] px-4 py-3 text-sm text-[#1A1B1A] leading-relaxed bg-white focus:outline-none [&_h2]:text-base [&_h2]:font-semibold [&_h2]:mt-4 [&_h2]:mb-1 [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:mt-3 [&_p]:mb-2 [&_ul]:list-disc [&_ol]:list-decimal [&_ul]:pl-5 [&_ol]:pl-5"
      />
    </div>
  );
}
